/**
 * RawProductAutocomplete Component
 *
 * Autocomplete for searching raw products to add to the supplier's catalog.
 * Products can be added multiple times with different packagings.
 * @see US-8.7 - Drawer Gestion des References
 */

import { useState, useMemo, useCallback } from 'react';
import { Package, Check } from 'lucide-react';
import { useDebounce } from '@/hooks';
import { useRawProducts } from '@/features/raw-products/hooks/useRawProducts';
import { SearchAutocomplete, type SearchAutocompleteOption } from '@/components/ui';
import type { RawProductWithRelations, SupplierCatalogItemWithDetails } from '@bstock/shared';

export interface RawProductAutocompleteProps {
  /** Products already in the catalog (to show indicator) */
  existingCatalogItems: SupplierCatalogItemWithDetails[];
  /** Placeholder text */
  placeholder?: string;
  /** Callback when a raw product is selected */
  onSelect: (product: RawProductWithRelations) => void;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Custom class name */
  className?: string;
}

/** Extended option type that includes the full raw product data */
interface RawProductOption extends SearchAutocompleteOption {
  product: RawProductWithRelations;
  isInCatalog: boolean;
}

export function RawProductAutocomplete({
  existingCatalogItems,
  placeholder = 'Rechercher un produit brut a ajouter...',
  onSelect,
  disabled = false,
  className,
}: RawProductAutocompleteProps) {
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearch = useDebounce(searchValue, 300);

  // Get IDs of products already in catalog (for indicator only, not filtering)
  const existingProductIds = useMemo(
    () => new Set(existingCatalogItems.map((item) => item.rawProductId)),
    [existingCatalogItems]
  );

  // Fetch raw products only when user has typed something
  const hasSearch = debouncedSearch.length >= 1;
  const { data: productsData, isLoading } = useRawProducts({
    search: hasSearch ? debouncedSearch : undefined,
    isActive: true,
    limit: 50,
    sortBy: 'name',
    sortOrder: 'asc',
  }, { enabled: hasSearch });

  // Transform products to options format
  const options = useMemo<RawProductOption[]>(() => {
    const products = productsData?.data ?? [];
    return products.map((product) => ({
      id: product.id,
      label: product.name,
      sublabel: product.sku,
      product,
      isInCatalog: existingProductIds.has(product.id),
    }));
  }, [productsData?.data, existingProductIds]);

  // Handle selection - extract full product and pass to onSelect
  const handleChange = useCallback(
    (option: RawProductOption | null) => {
      if (option) {
        onSelect(option.product);
      }
    },
    [onSelect]
  );

  // Custom option renderer
  const renderOption = useCallback(
    (option: RawProductOption) => (
      <div className="flex items-start justify-between gap-3 py-1">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-dark-500 truncate">{option.product.name}</span>
            {option.isInCatalog && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded bg-success-100 text-success-700">
                <Check className="w-3 h-3" />
                Deja reference
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1 text-xs text-neutral-500">
            <Package className="w-3 h-3" />
            <span>SKU: {option.product.sku}</span>
            <span className="text-neutral-300">|</span>
            <span>Unite: {option.product.recipeUnit.symbol}</span>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <span className="inline-block px-2 py-0.5 text-xs rounded bg-neutral-100 text-neutral-600">
            {option.product.category.name}
          </span>
        </div>
      </div>
    ),
    []
  );

  return (
    <SearchAutocomplete<RawProductOption>
      value={null}
      onChange={handleChange}
      options={options}
      isLoading={isLoading}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      emptyMessage="Aucun produit brut trouve"
      minSearchLength={1}
      renderOption={renderOption}
      keepFocusOnSelect
    />
  );
}
